import { useEffect } from "react";
import { Box, Grid } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import { settings } from "app";
import BackButtonToolbar from "features/BackButtonToolbar";
import {
  FunctionField,
  Labeled,
  Show,
  SimpleShowLayout,
  TextField,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { DoctorantCandidacyStatusChoices, DoctorantCandidacyStatusColorMap } from "utils/choices";
import { DoctorantCandidacyStatus } from "utils/constants";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import StatusChip from "../../Partials/StatusChip";
import PersonalInfo from "./PersonalInfo";
import PlaceOfBirth from "./PlaceOfBirth";
import Citizenship from "./Citizenship";
import PreviousEducations from "./PreviousEducations";
import DocumentSection from "./DocumentSection";
import ObjectionSection from "./ObjectionSection";
import SeatsSection from "./SeatsSection";
import ExamsSection from "./ExamsSection";
import Payment from "./Payment";
import ActionButtons from "./ActionButtons";
import DoctoralDateField from "../../Partials/DoctoralDateField";
import { SectionTitle } from "features/SectionTitle";

const ClaimManager = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const recordId = record?.id;
  useEffect(() => {
    if (!recordId) return undefined;

    const claim = async () => {
      try {
        await dataProvider.customRequest(doctoralRequests.claimCandidacy(recordId), null, "POST");
      } catch (err) {
        console.error("Неуспешно заявяване на кандидатурата", err);
      } finally {
        refresh();
      }
    };

    claim();

    return () => {
      const release = async () => {
        try {
          await dataProvider.customRequest(doctoralRequests.releaseCandidacy(recordId), null, "POST");
        } catch (err) {
          console.error("Неуспешно освобождаване на кандидатурата", err);
        }
      };
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId]);
  return null;
};

const CandidacyStatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <StatusChip
      status={record.status}
      choices={DoctorantCandidacyStatusChoices}
      colorMap={DoctorantCandidacyStatusColorMap}
    />
  );
};

const Header = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={3}
      >
        <Labeled label="Вх. номер">
          <TextField
            source="registrationNumber"
            emptyText="—"
          />
        </Labeled>
      </Grid>
      <Grid
        item
        xs={12}
        md={3}
      >
        <Labeled label="Кампания">
          <TextField
            source="campaignNameBg"
            emptyText="—"
          />
        </Labeled>
      </Grid>
      <Grid
        item
        xs={12}
        md={2}
      >
        <Labeled label="Статус">
          <FunctionField render={() => <CandidacyStatusChip />} />
        </Labeled>
      </Grid>
      <Grid
        item
        xs={12}
        md={2}
      >
        <Labeled label="Подадена на">
          <DoctoralDateField
            source="createdOn"
            showTime
          />
        </Labeled>
      </Grid>
      <Grid
        item
        xs={12}
        md={2}
      >
        <Labeled label="Обработва се от">
          <TextField
            source="assignedToUserName"
            emptyText="—"
          />
        </Labeled>
      </Grid>
    </Grid>
  );
};

const ObjectionBlock = ({ shadow }) => {
  const record = useRecordContext();
  const relevant =
    record &&
    (record.status === DoctorantCandidacyStatus.NotAdmitted ||
      record.status === DoctorantCandidacyStatus.ObjectionUnderReview ||
      record.objectionDocumentId);
  if (!relevant) return null;
  return (
    <>
      <SectionTitle
        title="Възражение"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <Box sx={shadow}>
        <ObjectionSection />
      </Box>
    </>
  );
};

const ShowDoctorantCandidacy = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const shadow = isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox;

  return (
    <Show
      title="Кандидатура за докторант"
      actions={<BackButtonToolbar additionalButton={<ActionButtons />} />}
    >
      <SimpleShowLayout>
        <ClaimManager />

        <SectionTitle
          title="Данни за кандидата"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <PersonalInfo />
          <PlaceOfBirth />
          <Citizenship />
        </Box>

        <SectionTitle
          title="Данни за кандидатурата"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <Header />
        </Box>

        <SectionTitle
          title="Предишно образование"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <PreviousEducations />
        </Box>

        <SectionTitle
          title="Избрани места"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <SeatsSection />
        </Box>

        <SectionTitle
          title="Изпити"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <ExamsSection />
        </Box>

        <SectionTitle
          title="Документи"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <DocumentSection />
        </Box>

        <ObjectionBlock shadow={shadow} />

        <SectionTitle
          title="Плащане"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={shadow}>
          <Payment />
        </Box>
      </SimpleShowLayout>
    </Show>
  );
};

export default ShowDoctorantCandidacy;
